import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, Route, UrlSegment } from '@angular/router';
import { map, concatMap } from 'rxjs/operators';
import { OrigamiUrlsParams } from '@app/origami-api/constants';
import { LoginToRoomService, AuthService } from '@app/services';
import { SessionService } from '@app/session/session.service';
import { of } from 'rxjs';
import { AppRoutes } from '@app/app-routes';

@Injectable({
  providedIn: 'root',
})
export class CTMSDashboardPageGuard {
  constructor(
    private authService: AuthService,
    private router: Router,
    private sessionService: SessionService,
    private loginToRoomService: LoginToRoomService,
  ) {}

  canActivate = (route: ActivatedRouteSnapshot) => {
    return this.authService.checkSession().pipe(
      concatMap((auth) => {
        if (auth.hasSession && !auth.registrationMode) {
          if (auth.info.HasAccessToAnyRoom) {
            const roomId = +route.paramMap.get('roomId');

            return this.sessionService.enterDomain().pipe(
              concatMap(() => this.loginToRoomService.loginWithConfidentialityAgreementId(roomId)),
              concatMap((loginRes) => {
                if (!loginRes.error && loginRes.details) {
                  return this.sessionService
                    .enterRoom(roomId)
                    .pipe(map(() => ({ loggedIn: true, hasAccessToAnyRoom: true, enteredRoom: true })));
                }
                return of({ loggedIn: true, hasAccessToAnyRoom: true, enteredRoom: false });
              }),
            );
          }
          return of({ loggedIn: true, hasAccessToAnyRoom: false, enteredRoom: false });
        }
        return of({ loggedIn: false, hasAccessToAnyRoom: false, enteredRoom: false });
      }),
      map((res) => {
        this.goToAlterUrl({
          loggedIn: res.loggedIn,
          hasAccessToAnyRoom: res.hasAccessToAnyRoom,
          enteredRoom: res.enteredRoom,
          url: route.url.join('/'),
        });

        return res.loggedIn && res.hasAccessToAnyRoom && res.enteredRoom;
      }),
    );
  };

  canDeactivate = () =>
    this.sessionService.leaveRoom().pipe(
      concatMap(() => this.sessionService.leaveDomain()),
      map(() => true),
    );

  canLoad = (route: Route, segments: UrlSegment[]) =>
    this.authService.checkSession().pipe(
      map((auth) => {
        const loggedIn = auth.hasSession && !auth.registrationMode;

        this.goToAlterUrl({
          loggedIn: loggedIn,
          hasAccessToAnyRoom: loggedIn && auth.info.HasAccessToAnyRoom,
          url: segments.map((s) => s.path).join('/'),
        });

        return loggedIn;
      }),
    );

  private goToAlterUrl(res: { loggedIn: boolean; hasAccessToAnyRoom: boolean; enteredRoom?: boolean; url?: string }) {
    if (!res.loggedIn) {
      const queryParams = res.url ? { [OrigamiUrlsParams.returnRoute]: escape(res.url) } : undefined;
      this.router.navigate(['/auth'], { queryParams: queryParams });
    } else if (!res.hasAccessToAnyRoom) {
      this.router.navigate(['/no-access']);
    } else if (res.enteredRoom === false) {
      this.router.navigate([AppRoutes.Main]);
    }
  }
}
